import { useState, useEffect } from 'react';
import { collection, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

// Helper to check if URL is valid
const isValidImageUrl = (url) => url && typeof url === 'string' && url.match(/^https?:\/\/.+/);

// Helper to process a single site's data and determine mainImage
const processSiteData = (siteId, siteData) => {
  if (!siteData) return null; // Handle case where doc doesn't exist

  const site = { id: siteId, ...siteData };

  const media = (site.media || []).filter(m => 
    m && m.url && m.type === 'image' && isValidImageUrl(m.url)
  );

  const validMedia = media.length > 0 ? media : [];

  // If no valid media, use a proper fallback
  if (validMedia.length === 0) {
    validMedia.push({
      id: `placeholder-${site.id}`,
      type: 'image',
      url: '/textures/parchment.png',
      uploadedAt: new Date()
    });
  }

  // Determine the main image with the proper fallback chain:
  let mainImageUrl = site.mainImage && isValidImageUrl(site.mainImage) ? site.mainImage : null;

  if (!mainImageUrl && site.primaryMediaId) {
    const primaryMedia = validMedia.find(m => m.id === site.primaryMediaId);
    if (primaryMedia) {
      mainImageUrl = primaryMedia.url;
    }
  }

  if (!mainImageUrl && validMedia.length > 0) {
    mainImageUrl = validMedia[0].url;
  }
  
  // Ensure mainImageUrl is never null/undefined, use fallback if needed
  if (!mainImageUrl) {
    mainImageUrl = '/textures/parchment.png';
  }

  return {
    ...site,
    mainImage: mainImageUrl,
    media: validMedia // Return only valid media
  };
};

import { getLocalStorage, setLocalStorage } from '../utils/storage';

// Get a stable random seed for the day
const getDailySeed = () => {
  const today = new Date();
  return today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
};

// Key for local storage
const RANDOM_SITES_KEY = 'megalith_random_sites';
const RANDOM_SITES_DATE_KEY = 'megalith_random_sites_date';

export const useRandomSites = (count = 10) => {
  const [sites, setSites] = useState({
    data: [], 
    loading: true,
    error: null
  });
  const [siteIds, setSiteIds] = useState([]);

  // Check if we can use cached data
  useEffect(() => {
    const cachedDate = getLocalStorage(RANDOM_SITES_DATE_KEY);
    const todayStr = new Date().toDateString();
    
    // If we have cached data from today, use it
    if (cachedDate === todayStr) {
      const cachedSites = getLocalStorage(RANDOM_SITES_KEY);
      if (cachedSites?.length > 0) {
        setSites({
          data: cachedSites.slice(0, count),
          loading: false,
          error: null
        });
        setSiteIds(cachedSites.slice(0, count).map(site => site.id));
        return;
      }
    }

    // Otherwise fetch new data
    const fetchAndSelectSiteIds = async () => {
      try {
        setSites(prev => ({ ...prev, loading: true, error: null }));
        const sitesQuery = collection(db, 'sites');
        
        const seed = getDailySeed();
        
        const querySnapshot = await getDocs(sitesQuery);
        let allSiteIds = querySnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }));
        
        // Shuffle sites with daily seed
        for (let i = allSiteIds.length - 1; i > 0; i--) {
          const pseudoRandom = Math.sin(seed + i) * 10000;
          const j = Math.floor((pseudoRandom - Math.floor(pseudoRandom)) * (i + 1));
          [allSiteIds[i], allSiteIds[j]] = [allSiteIds[j], allSiteIds[i]];
        }

        // Take first 'count' items and process them
        const selectedSites = allSiteIds
          .slice(0, count)
          .map(({ id, data }) => processSiteData(id, data))
          .filter(Boolean);

        // Cache the data
        setLocalStorage(RANDOM_SITES_KEY, selectedSites);
        setLocalStorage(RANDOM_SITES_DATE_KEY, todayStr);

        // Update state
        setSites({
          data: selectedSites,
          loading: false,
          error: null
        });
        setSiteIds(selectedSites.map(site => site.id));

      } catch (error) {
        console.error('Failed to fetch site IDs:', error);
        setSites({ data: [], loading: false, error: 'Failed to load site list' });
      }
    };

    fetchAndSelectSiteIds();
  }, [count]);

  // Set up one-time listener for updates to any of our selected sites
  useEffect(() => {
    if (siteIds.length === 0) return;

    const unsubscribe = onSnapshot(
      collection(db, 'sites'),
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (siteIds.includes(change.doc.id)) {
            // Update the site in our local state
            setSites(prev => {
              const newData = [...prev.data];
              const index = newData.findIndex(site => site.id === change.doc.id);
              if (index !== -1) {
                const processedData = processSiteData(change.doc.id, change.doc.data());
                if (processedData) {
                  newData[index] = processedData;
                }
              }
              return { ...prev, data: newData };
            });
          }
        });
      },
      (error) => {
        console.error('Error listening to sites updates:', error);
      }
    );

    return () => unsubscribe();
  }, [siteIds]);

  return {
    ...sites,
    data: sites.data.map(site => ({
      ...site,
      mainImage: site.mainImage || '/textures/parchment.png',
      name: site.name || 'Untitled Site',
      description: site.description || '',
    }))
  };
};
